/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Check,
  Cross,
  InfoCircle,
  AlertCircle,
  CircleCheck,
  RotateClockwiseDots,
} from '@saleshandy/icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SkeletonLoading } from '@saleshandy/design-system';
import { Spinner } from 'react-bootstrap';
import Modal from '../../../../../../../../../../shared/design-system/components/atoms/modal';
import Table from '../../../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../../../shared/design-system/components/organisms/table/types';
import {
  OverlayTooltip,
  OverlayPopover,
  Placement,
} from '../../../../../../../../../../shared/design-system/components/overlay';
import { EmailAccountAuthentication } from '../../../../../../../../types';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';
import FactorDetailsModal from '../../../../../../../../../../shared/components/email-account-setup-score/components/factor-details-modal';
import { FactorModalConfig } from '../../../../../../../../../../shared/components/email-account-setup-score/types';
import {
  getCopyContent,
  getDomainBlacklistContent,
  getDomainBlacklistFooter,
  getIPBlacklistContent,
  getIPBlacklistFooter,
  getLearnMoreContent,
  getPtrSummaryString,
} from '../../../../../../../../../../shared/components/email-account-setup-score/get-descriptions';
import { supportUrls } from '../../../../../../../../../../shared/utils/urls';
import {
  getBlacklistedCount,
  getValidBlackistURLs,
  VALID_BLACKLIST_URLS,
} from '../../../../../../../../../../shared/components/email-account-setup-score/helper';
import { isPtrStatusNotValid } from "../../../../../../../../../email-accounts/enums/email-account";
import YoutubeVideoModal from '../../../../../../../../../../shared/design-system/components/molecules/youtube-video-modal';
import { getEspIcon } from '../../../utils/esp-icon';

// Add InfoPopover component similar to email authentication table
const InfoPopover = ({
  popoverKey,
  isOpen,
  onToggle,
  tooltipRef,
}: {
  popoverKey: string;
  isOpen: boolean;
  onToggle: (show: boolean) => void;
  tooltipRef: (el: HTMLElement | null) => void;
}) => {
  const { t } = useTranslation();

  // Define popover content for each type
  const getPopoverContent = (key: string) => {
    switch (key) {
      case 'spf':
        return {
          description:
            t('messages.spf_records_verified_description') ||
            'Sender Policy Framework provides email authentication',
          learnMoreUrl: supportUrls.spfBlog,
        };
      case 'dkim':
        return {
          description:
            t('messages.dkim_records_verified_description') ||
            'DomainKeys Identified Mail provides email authentication',
          learnMoreUrl: supportUrls.dkimBlog,
        };
      case 'dmarc':
        return {
          description:
            t('messages.dmarc_records_verified_description') ||
            'Domain-based Message Authentication, Reporting and Conformance',
          learnMoreUrl: supportUrls.dmarcBlog,
        };
        case 'domainBlacklist':
          return {
            description: 'Checks if your domain is listed on any blacklists',
            learnMoreUrl: supportUrls.domainAndIPBlacklistBlog,
          };
          case 'ipBlacklist':
            return {
              description: 'Checks if your IP is listed on any blacklists',
              learnMoreUrl: supportUrls.domainAndIPBlacklistBlog,
            };
            case 'ptr':
              return {
                description:
                  'Helps mail servers verify that the IP and domain are aligned, reducing spam suspicion and improving overall deliverability.',
                learnMoreUrl: supportUrls.ptrBlog,
              };
      default:
        return {
          description: '',
          learnMoreUrl: '',
        };
    }
  };

  const content = getPopoverContent(popoverKey);

  return (
    <div ref={tooltipRef}>
      <OverlayPopover
        className="permission-popover"
        show={isOpen}
        onToggle={onToggle}
        placement={Placement.Bottom}
        content={
          <>
            <div className="content">
              {content.description}{' '}
              <a
                href={content.learnMoreUrl}
                target="_blank"
                rel="noreferrer"
                className="link"
                style={{ whiteSpace: 'nowrap' }}
              >
                Learn More
              </a>
            </div>
            <div
              className="d-flex justify-content-center"
              {...accessibleOnClick(() => onToggle(false))}
            >
              <Cross className="cross-icon" />
            </div>
          </>
        }
      >
        <InfoCircle
          className={`info-icon-popover-btn cursor-pointer ${
            isOpen ? 'isInfoIconVisible' : 'isInfoIconNotVisible'
          }`}
        />
      </OverlayPopover>
    </div>
  );
};

const EmailAuthenticationReportModal: React.FC<{
  show: boolean;
  onClose: () => void;
  selectedEmailAccountId?: number;
  onEmailAccountChange?: (emailAccountId: number) => void;
  emailReputationData?: any;
  hasMoreEmailReputation?: boolean;
  isLoading?: boolean;
  error?: any;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}> = ({
  show,
  onClose,
  emailReputationData,
  isLoading,
  hasMoreEmailReputation,
  onLoadMore,
  isLoadingMore,
}) => {
  const { t } = useTranslation();
  const [showFactorModal, setShowFactorModal] = useState(false);
  const [factorModalConfig, setFactorModalConfig] = useState<FactorModalConfig>(
    null,
  );
  const [showYoutubeVideoModal, setShowYoutubeVideoModal] = useState(false);
  const [youtubeVideoURL, setYoutubeVideoURL] = useState('');
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Add state for managing popovers
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const tooltipRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleClose = () => {
    onClose();
  };

  const handleScroll = useCallback(() => {
    if (modalContentRef?.current) {
      const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
      // Load more when the user is 200px from the bottom
      if (scrollHeight - scrollTop <= clientHeight + 200) {
        if (hasMoreEmailReputation && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      }
    }
  }, [hasMoreEmailReputation, isLoadingMore, onLoadMore]);

  useEffect(() => {
    const container = modalContentRef?.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Add effect for managing popovers
  useEffect(() => {
    if (!activePopover) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!tooltipRefs.current[activePopover]?.contains(event.target as Node)) {
        setActivePopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopover]);

  useEffect(
    () => () => {
      tooltipRefs.current = {};
    },
    [],
  );

  const showYoutubeVideoHandler = (url: string) => {
    setYoutubeVideoURL(url);
    setShowYoutubeVideoModal(true);
  };

  const hideYoutubeVideoHandler = () => {
    setShowYoutubeVideoModal(false);
  };

  const handleFactorModal = (
    record: EmailAccountAuthentication,
    modalName: string,
  ) => {
    let modalConfig: FactorModalConfig = {
      title: '',
    };

    switch (modalName) {
      case 'spf':
        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {record?.authReputation?.spf?.isValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {record?.authReputation?.spf?.isValid
                  ? t('messages.spf_records_verified')
                  : t('messages.spf_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {record?.authReputation?.spf?.isValid
                    ? t('messages.spf_records_verified_description')
                    : t('messages.spf_record_not_found_description')}
                </span>
              </div>
              {record?.authReputation?.spf?.isValid
                ? getCopyContent(record?.authReputation?.spf?.spfResults)
                : getLearnMoreContent('SPF', supportUrls.spfBlog)}
            </>
          ),
          width: 'small',
        };
        break;
      case 'dmarc':
        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {record?.authReputation?.dmarc?.isValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {record?.authReputation?.dmarc?.isValid
                  ? t('messages.dmarc_records_verified')
                  : t('messages.dmarc_record_not_detected')}
              </span>
            </span>
          ),
          content: record?.authReputation?.dmarc?.isValid ? (
            <>
              <div className="modal-description">
                <span>
                  {record?.authReputation?.dmarc?.isValid
                    ? t('messages.dmarc_records_verified_description')
                    : t('messages.dmarc_record_not_detected_description')}
                </span>
              </div>
              {record?.authReputation?.dmarc?.isValid
                ? getCopyContent(record?.authReputation?.dmarc?.dmarcResults)
                : getLearnMoreContent('DMARC', supportUrls.dmarcBlog)}
            </>
          ) : (
            <>
              <div className="modal-description">
                <span>
                  {t('messages.dmarc_record_not_detected_description')}
                </span>
              </div>
            </>
          ),
          width: 'small',
        };
        break;
      case 'dkim':
        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {record?.authReputation?.dkim?.isValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {record?.authReputation?.dkim?.isValid
                  ? t('messages.dkim_records_verified')
                  : t('messages.dkim_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {record?.authReputation?.dkim?.isValid
                    ? t('messages.dkim_records_verified_description')
                    : t('messages.dkim_record_not_found_description')}
                </span>
              </div>
              {record?.authReputation?.dkim?.isValid
                ? getCopyContent(
                    record?.authReputation?.dkim?.dkimRecord?.join(','),
                  )
                : getLearnMoreContent('DKIM', supportUrls.dkimBlog)}
            </>
          ),
          width: 'small',
        };
        break;
      case 'ptr': {
        const ptr = record?.authReputation?.ptr;
        const ptrValid =
          ptr != null && !isPtrStatusNotValid(ptr?.status);
        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {ptrValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {ptrValid
                  ? t('messages.ptr_records_verified')
                  : t('messages.ptr_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {ptrValid
                    ? t('messages.ptr_records_verified_description')
                    : t('messages.ptr_record_not_found_description')}
                </span>
              </div>
              {ptrValid ? (
                ptr?.status === 'not_required' ? (
                  getCopyContent(t('messages.ptr_managed_by_provider'))
                ) : (
                  getCopyContent(
                    getPtrSummaryString(
                      ptr?.sending_ip ?? '',
                      record?.authReputation?.domainBlacklist?.domain ?? '',
                    ),
                  )
                )
              ) : (
                getLearnMoreContent('PTR', supportUrls.ptrBlog)
              )}
            </>
          ),
          width: 'small',
        };
        break;
      }
      case 'domainBlacklist':
        if (record?.authReputation?.domainBlacklist?.data?.length > 0) {
          const domainBlacklistData = getValidBlackistURLs(
            record?.authReputation?.domainBlacklist?.data,
          );
          modalConfig = {
            title: t('messages.domain_blacklist_title'),
            content: record?.authReputation?.domainBlacklist
              ? getDomainBlacklistContent({
                  domain: record.authReputation?.domainBlacklist.domain,
                  ip: record.authReputation?.domainBlacklist.ip,
                  data: record.authReputation?.domainBlacklist.data,
                })
              : null,
            columns: [
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                align: 'left',
              },
              {
                title: t('messages.modal_col_blacklist'),
                dataIndex: 'blacklist',
                key: 'blacklist',
                align: 'left',
                render: (cell) => (
                  <span>
                    {VALID_BLACKLIST_URLS[cell] !== '' ? (
                      <a
                        href={VALID_BLACKLIST_URLS[cell]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cell}
                      </a>
                    ) : (
                      cell
                    )}
                  </span>
                ),
              },
            ],
            data: domainBlacklistData,
            footer:
              getBlacklistedCount(
                record?.authReputation?.domainBlacklist?.data,
              ) > 0
                ? getDomainBlacklistFooter(() =>
                    showYoutubeVideoHandler(
                      supportUrls.domainIPBlacklistVideoUrl,
                    ),
                  )
                : null,
            onLearnMore: () => {
              window.open(supportUrls.domainAndIPBlacklistBlog, '_blank');
              setShowFactorModal(false);
            },
          };
          break;
        }
        break;
      case 'ipBlacklist':
        if (record?.authReputation?.ipBlacklist?.data?.length > 0) {
          const ipBlacklistData = getValidBlackistURLs(
            record?.authReputation?.ipBlacklist?.data,
          );
          modalConfig = {
            title: t('messages.ip_blacklist_title'),
            content: getIPBlacklistContent(record?.authReputation?.ipBlacklist),
            columns: [
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                align: 'left',
              },
              {
                title: t('messages.modal_col_blacklist'),
                dataIndex: 'blacklist',
                key: 'blacklist',
                align: 'left',
                render: (cell) => (
                  <span>
                    {VALID_BLACKLIST_URLS[cell] !== '' ? (
                      <a
                        href={VALID_BLACKLIST_URLS[cell]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cell}
                      </a>
                    ) : (
                      cell
                    )}
                  </span>
                ),
              },
            ],
            data: ipBlacklistData,
            footer:
              getBlacklistedCount(record?.authReputation?.ipBlacklist?.data) > 0
                ? getIPBlacklistFooter(() =>
                    showYoutubeVideoHandler(
                      supportUrls.domainIPBlacklistVideoUrl,
                    ),
                  )
                : null,
            onLearnMore: () => {
              window.open(supportUrls.domainAndIPBlacklistBlog, '_blank');
              setShowFactorModal(false);
            },
          };
          break;
        }
        break;
      default:
        break;
    }
    if (modalConfig.title === '') {
      return;
    }
    setFactorModalConfig(modalConfig);
    setShowFactorModal(true);
  };

  const renderBlacklistCell = (type: 'domainBlacklist' | 'ipBlacklist') => (
    cell: any,
    row: any,
  ) => {
    const cellClassName = classNames('cursor-pointer', {
      'font-weight-medium': row?.authReputation?.[type]?.listedCount,
      'color-red': row?.authReputation?.[type]?.listedCount > 2,
      'color-orange':
        row?.authReputation?.[type]?.listedCount <= 2 &&
        row?.authReputation?.[type]?.listedCount > 0,
      'color-green': row?.authReputation?.[type]?.listedCount === 0,
    });
    if (row?.authReputation?.[type]?.isFreeDomainOrIp) {
      return (
        <OverlayTooltip
          className="blacklist-tooltip"
          placement={Placement.Bottom}
          text="For free domains we don't check blacklist"
        >
          <span className="blacklist-tooltip">-</span>
        </OverlayTooltip>
      );
    }
    if (row?.authReputation?.[type]?.listedCount === -1) {
      return (
        <OverlayTooltip
          className="blacklist-tooltip"
          placement={Placement.Bottom}
          text="In-progress"
        >
          <RotateClockwiseDots />
        </OverlayTooltip>
      );
    }

    return (
      <div
        className={cellClassName}
        role="button"
        tabIndex={0}
        onClick={() => handleFactorModal(row, type)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') handleFactorModal(row, type);
        }}
      >
        {row?.authReputation?.[type]?.listedCount >= 0 ? (
          <OverlayTooltip text="View List" placement={Placement.Bottom}>
            <span>{row.authReputation[type].listedCount}</span>
          </OverlayTooltip>
        ) : (
          <span>-</span>
        )}
      </div>
    );
  };
  return (
    <Modal
      show={show}
      onClose={handleClose}
      onHide={handleClose}
      titleContent={
        <div className="spam-assassin-report-modal-header">
          <strong>Email authentication needs to be set up</strong>
        </div>
      }
      className="email-authentication-report-modal-container"
      hideCancelButton={true}
      hideSubmitButton={true}
      hideFooter={true}
      showCloseIcon={true}
      backdrop="static"
    >
      <div className="spam-assassin-report-modal-table" ref={modalContentRef}>
        <Table
          columns={[
            {
              dataField: 'fromEmail',
              text: 'Email Address',
              headerAlign: 'start',
              component: (cell, row) => (
                <span className="email-address">
                  {getEspIcon(row?.esp)}
                  <OverlayTooltip text={cell} placement={Placement.Bottom}>
                    <span className="email-name">{cell}</span>
                  </OverlayTooltip>
                </span>
              ),
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'authReputation.spf',
              text: 'SPF',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>SPF</span>
                  <InfoPopover
                    popoverKey="spf"
                    isOpen={activePopover === 'spf'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'spf' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.spf = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => (
                <div
                  className="cursor-pointer"
                  {...accessibleOnClick(() => handleFactorModal(row, 'spf'))}
                >
                  {cell?.isValid ? (
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text="View Record"
                    >
                      <Check className="check-cross-icon" color="#059669" />
                    </OverlayTooltip>
                  ) : (
                    <OverlayTooltip
                      className="blacklist-tooltip"
                      placement={Placement.Bottom}
                      text="View Setup guide"
                    >
                      <Cross className="check-cross-icon" color="#DC2626" />
                    </OverlayTooltip>
                  )}
                </div>
              ),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.dkim',
              text: 'DKIM',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>DKIM</span>
                  <InfoPopover
                    popoverKey="dkim"
                    isOpen={activePopover === 'dkim'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'dkim' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.dkim = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => (
                <div
                  className="cursor-pointer"
                  {...accessibleOnClick(() => handleFactorModal(row, 'dkim'))}
                >
                  {cell?.isValid ? (
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text="View Record"
                    >
                      <Check className="check-cross-icon" color="#059669" />
                    </OverlayTooltip>
                  ) : (
                    <OverlayTooltip
                      className="blacklist-tooltip"
                      placement={Placement.Bottom}
                      text="View Setup guide"
                    >
                      <Cross className="check-cross-icon" color="#DC2626" />
                    </OverlayTooltip>
                  )}
                </div>
              ),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.dmarc',
              text: 'DMARC',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>DMARC</span>
                  <InfoPopover
                    popoverKey="dmarc"
                    isOpen={activePopover === 'dmarc'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'dmarc' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.dmarc = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => (
                <div
                  className="cursor-pointer"
                  {...accessibleOnClick(() => handleFactorModal(row, 'dmarc'))}
                >
                  {cell?.isValid ? (
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text="View Record"
                    >
                      <Check className="check-cross-icon" color="#059669" />
                    </OverlayTooltip>
                  ) : (
                    <OverlayTooltip
                      className="blacklist-tooltip"
                      placement={Placement.Bottom}
                      text="View Setup guide"
                    >
                      <Cross className="check-cross-icon" color="#DC2626" />
                    </OverlayTooltip>
                  )}
                </div>
              ),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.ptr',
              text: 'PTR (rDNS)',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>PTR (rDNS)</span>
                  <InfoPopover
                    popoverKey="ptr"
                    isOpen={activePopover === 'ptr'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'ptr' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.ptr = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => (
                <div
                  className="cursor-pointer"
                  {...accessibleOnClick(() => handleFactorModal(row, 'ptr'))}
                >
                  {!isPtrStatusNotValid(cell?.status) ? (
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text="View Record"
                    >
                      <Check className="check-cross-icon" color="#059669" />
                    </OverlayTooltip>
                  ) : (
                    <OverlayTooltip
                      className="blacklist-tooltip"
                      placement={Placement.Bottom}
                      text="View Setup guide"
                    >
                      <Cross className="check-cross-icon" color="#DC2626" />
                    </OverlayTooltip>
                  )}
                </div>
              ),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.domainBlacklist',
              text: 'Domain Blacklist',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>Domain Blacklist</span>
                  <InfoPopover
                    popoverKey="domainBlacklist"
                    isOpen={activePopover === 'domainBlacklist'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'domainBlacklist' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.domainBlacklist = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) =>
                renderBlacklistCell('domainBlacklist')(cell, row),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.ipBlacklist',
              text: 'IP Blacklist',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>IP Blacklist</span>
                  <InfoPopover
                    popoverKey="ipBlacklist"
                    isOpen={activePopover === 'ipBlacklist'}
                    onToggle={(showPopover) =>
                      setActivePopover(showPopover ? 'ipBlacklist' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.ipBlacklist = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) =>
                renderBlacklistCell('ipBlacklist')(cell, row),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
          ]}
          data={emailReputationData || []}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              limit: emailReputationData?.length || 0,
              page: 1,
              totalElements: emailReputationData?.length || 0,
            },
          }}
          isLoading={isLoading && !isLoadingMore}
        />
        {isLoadingMore && (
          <div className="d-flex justify-content-center align-items-center m-4">
            <Spinner className="spinner--blue" animation="border" />
          </div>
        )}
      </div>
      <FactorDetailsModal
        show={showFactorModal}
        onClose={() => setShowFactorModal(false)}
        factorModalConfig={factorModalConfig}
      />
      <YoutubeVideoModal
        show={showYoutubeVideoModal}
        youtubeVideoUrl={youtubeVideoURL}
        onClose={hideYoutubeVideoHandler}
      />
    </Modal>
  );
};

export default EmailAuthenticationReportModal;
